import { SoldToAddress } from "@dana/Types/ApiModels";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const cartMode = getCookie('cartMode');
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
        shippedToText: cartMode === "AnnualReturnsOrderCart" ? "Shipped from" : "Shipped to",
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationBillingAndShippingStyles {
    container?: GridContainerProps;
    shipToGridItem?: GridItemProps;
    shipToTitleText?: TypographyPresentationProps;
    shipToText?: TypographyPresentationProps;
    shipToAddress?: AddressInfoDisplayStyles;
    pickUpGridItem?: GridItemProps;
    pickUpTitleText?: TypographyPresentationProps;
    pickUpText?: TypographyPresentationProps;
    pickUpAddress?: AddressInfoDisplayStyles;
    billToGridItem?: GridItemProps;
    billToTitleText?: TypographyPresentationProps;
    billToText?: TypographyPresentationProps;
}

export const billingAndShippingStyles: OnePageCheckoutConfirmationBillingAndShippingStyles = {
    container: { gap: 20 },
    shipToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    shipToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    billToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = billingAndShippingStyles;

const OnePageCheckoutConfirmationBillingAndShipping = ({ cart, billTo, shipTo, shippedToText }: Props) => {
    if (!cart || !billTo || !shipTo) {
        return null;
    }
    const soldToObj: SoldToAddress | undefined = cart?.properties?.soldToAddress
        ? JSON.parse(cart.properties.soldToAddress)
        : undefined;
    let isSoldBillTo;
    if (soldToObj?.Id === billTo?.id) {
        isSoldBillTo = "1";
    } else if (soldToObj?.Id !== billTo?.id) {
        isSoldBillTo = "0";
    }
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.billToGridItem} data-test-selector="onePageCheckOutConfirmationBillingInformation">
                <Typography as="h3" {...styles.billToTitleText}>
                    {translate("Sold to")} - {isSoldBillTo === "1" ?billTo.customerSequence:soldToObj?.CustomerSequence}
                </Typography>
                <Typography as="div" {...styles.billToText}>
                    {isSoldBillTo === "1" ? (
                        <AddressInfoDisplay
                            extendedStyles={styles.pickUpAddress}
                            address1={billTo.address1}
                            address2={billTo.address2}
                            city={billTo.city}
                            postalCode={billTo?.postalCode || ""}
                            firstName={billTo?.firstName || ""}
                            lastName={billTo?.lastName || ""}
                            companyName={billTo?.companyName || ""}
                            state={billTo.state?.abbreviation}
                            country={billTo.country?.name}
                        />
                    ) : (
                        <AddressInfoDisplay
                            extendedStyles={styles.pickUpAddress}
                            address1={soldToObj?.Address1 || ""}
                            address2={soldToObj?.Address2 || ""}
                            city={soldToObj?.City || ""}
                            postalCode={soldToObj?.PostalCode || ""}
                            firstName={soldToObj?.FirstName || ""}
                            lastName={soldToObj?.LastName || ""}
                            companyName={soldToObj?.CompanyName || ""}
                            state={soldToObj?.State}
                            country={soldToObj?.CountryName}
                        />
                    )}
                </Typography>
            </GridItem>
            {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                <GridItem
                    {...styles.shipToGridItem}
                    data-test-selector="onePageCheckOutConfirmationShippingInformation"
                >
                    <Typography as="h3" {...styles.shipToTitleText}>
                        {translate(shippedToText)} - {shipTo.customerSequence}
                    </Typography>
                    <AddressInfoDisplay
                        address1={shipTo.address1}
                        address2={shipTo.address2}
                        city={shipTo.city}
                        postalCode={shipTo?.postalCode || ""}
                        firstName={shipTo?.firstName || ""}
                        lastName={shipTo?.lastName || ""}
                        companyName={shipTo?.companyName || ""}
                        state={shipTo.state?.abbreviation}
                        country={shipTo.country?.name}
                        extendedStyles={styles.shipToAddress}
                    />
                </GridItem>
            )}

            <GridItem {...styles.billToGridItem} data-test-selector="onePageCheckOutConfirmationBillingInformation">
                <Typography as="h3" {...styles.billToTitleText}>
                    {translate("Billed to")} - {billTo.customerSequence}
                </Typography>
                <Typography as="div" {...styles.billToText}>
                    <AddressInfoDisplay
                        extendedStyles={styles.pickUpAddress}
                        address1={billTo.address1}
                        address2={billTo.address2}
                        city={billTo.city}
                        postalCode={billTo?.postalCode || ""}
                        firstName={billTo?.firstName || ""}
                        lastName={billTo?.lastName || ""}
                        companyName={billTo?.companyName || ""}
                        state={billTo.state?.abbreviation}
                        country={billTo.country?.name}
                    />
                </Typography>
            </GridItem>
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationBillingAndShipping),
    definition: {
        displayName: "Billing And Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
